import './bootstrap';
import '../css/app.css';

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createI18n } from 'vue-i18n';
// Plan 01-09 — vue-i18n v12.0.0-alpha.2 declares `sideEffects: false` in its
// package.json, which causes Rollup's tree-shaker to drop the top-level
// `registerMessageCompiler(compile)` side-effect call inside `vue-i18n.js`.
// Without that registration, the AST-form messages emitted by
// @intlify/unplugin-vue-i18n's `jit: true` mode aren't evaluated at runtime and
// vue-i18n returns the translation key verbatim — which IS the BUGS-01b /
// BUGS-02b production regression. Calling registerMessageCompiler here from a
// "used" import keeps the call alive through tree-shaking. This is the
// upstream-recommended workaround for the v12-alpha sideEffects regression
// (see github.com/intlify/vue-i18n).
import {
    registerMessageCompiler,
    compile,
    registerMessageResolver,
    resolveValue as i18nResolveValue,
    registerLocaleFallbacker,
    fallbackWithLocaleChain,
} from '@intlify/core-base';
import { ZiggyVue } from '../../vendor/tightenco/ziggy';
import enModule from './i18n/locales/en';
import ptModule from './i18n/locales/pt';
import { normalizeLocale } from './lib/locale';

registerMessageCompiler(compile);
registerMessageResolver(i18nResolveValue);
registerLocaleFallbacker(fallbackWithLocaleChain);

function unwrapMessages(moduleValue) {
    let value = moduleValue;

    while (value && typeof value === 'object' && 'default' in value) {
        value = value.default;
    }

    return value && typeof value === 'object' ? value : {};
}

const en = unwrapMessages(enModule);
const pt = unwrapMessages(ptModule);
const messages = { en, pt };

function getMessageByPath(source, path) {
    if (!source || typeof source !== 'object' || typeof path !== 'string') {
        return undefined;
    }

    return path.split('.').reduce((value, segment) => {
        if (!value || typeof value !== 'object' || !(segment in value)) {
            return undefined;
        }

        return value[segment];
    }, source);
}

const i18n = createI18n({
    legacy: false,
    locale: normalizeLocale(document.documentElement.lang),
    fallbackLocale: 'en',
    globalInjection: true,
    messages,
});

i18n.global.setLocaleMessage('en', en);
i18n.global.setLocaleMessage('pt', pt);

const fallbackMessages = { en, pt };
const originalT = i18n.global.t.bind(i18n.global);
const originalTm = i18n.global.tm.bind(i18n.global);
const originalTe = i18n.global.te.bind(i18n.global);

function extractNamedInterpolationValues(args) {
    for (const arg of args) {
        if (arg !== null && typeof arg === 'object' && !Array.isArray(arg)) {
            return arg;
        }
    }

    return undefined;
}

function applyNamedInterpolation(template, values) {
    if (typeof template !== 'string' || values === undefined || values === null) {
        return template;
    }

    return template.replace(/\{(\w+)\}/g, (match, name) =>
        Object.prototype.hasOwnProperty.call(values, name) &&
        values[name] !== undefined &&
        values[name] !== null
            ? String(values[name])
            : match
    );
}

function resolveFallbackValue(key, localeValue) {
    const normalizedLocale = normalizeLocale(localeValue || i18n.global.locale.value);
    const primary = getMessageByPath(fallbackMessages[normalizedLocale], key);
    if (primary !== undefined) {
        return primary;
    }

    const secondaryLocale = normalizedLocale === 'pt' ? 'en' : 'pt';
    return getMessageByPath(fallbackMessages[secondaryLocale], key);
}

i18n.global.t = (key, ...args) => {
    const namedValues = extractNamedInterpolationValues(args);
    const translated = originalT(key, ...args);

    if (typeof key !== 'string') {
        return translated;
    }

    if (translated !== key && namedValues) {
        const patched = applyNamedInterpolation(translated, namedValues);
        if (patched !== translated) {
            return patched;
        }
    }

    if (translated !== key) {
        return translated;
    }

    const localeHint = typeof args[0] === 'string' ? args[0] : undefined;
    const fallback = resolveFallbackValue(key, localeHint);
    if (typeof fallback === 'string' && namedValues) {
        return applyNamedInterpolation(fallback, namedValues);
    }

    return typeof fallback === 'string' ? fallback : translated;
};

i18n.global.tm = key => {
    const translated = originalTm(key);
    if (typeof key !== 'string' || translated !== key) {
        return translated;
    }

    return resolveFallbackValue(key, i18n.global.locale.value);
};

i18n.global.te = (key, localeValue) => {
    if (originalTe(key, localeValue)) {
        return true;
    }

    return resolveFallbackValue(key, localeValue) !== undefined;
};

function syncVueI18nLocaleFromPage(page) {
    const raw = page?.props?.lang;
    if (raw === undefined || raw === null || raw === '') {
        return;
    }

    const normalized = normalizeLocale(String(raw));
    i18n.global.locale.value = normalized;
    document.documentElement.lang = normalized;
}

router.on('navigate', event => {
    syncVueI18nLocaleFromPage(event.detail.page);
});

router.on('success', event => {
    syncVueI18nLocaleFromPage(event.detail.page);
});

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

createInertiaApp({
    title: () => appName,
    resolve: name =>
        resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        const pageLang = props.initialPage?.props?.lang;
        i18n.global.locale.value = normalizeLocale(pageLang || document.documentElement.lang);
        document.documentElement.lang = i18n.global.locale.value;

        if (!i18n.global.te('nav_public.howItWorks')) {
            i18n.global.mergeLocaleMessage('en', en);
            i18n.global.mergeLocaleMessage('pt', pt);
        }

        return app.use(plugin).use(i18n).use(ZiggyVue).mount(el);
    },
    progress: {
        color: 'rgb(var(--color-primary-600))',
    },
});
